import React, { useMemo, useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import Field, { fieldTypes } from '../../../../../../shared/ui/Field';
import { fieldNames } from '../../../config';
import {
  useAddressesTreeData,
  useLoadTreeData,
  generateDataForTree,
} from '../../../../../../shared/lib/treeAddressesUtils';
import { treeCheckThemes } from '../../../../../../../../components/TreeCheck';
import {
  useNewCityValueSubscription,
  useNewCompanyValueSubscription,
} from '../../../lib/subscriptions';
import { shutdownCitiesQuery } from '../../../../../../shared/api/queries';
import styles from './AddressField.module.scss';

const { key: citiesKey } = shutdownCitiesQuery;
const fieldName = fieldNames.addresses;
const cityFieldName = fieldNames.city;
const companyFieldName = fieldNames.company;

const getLabelFromInitialValue = (initialValue) => {
  const name = initialValue.city?.name ? ` (${initialValue.city?.name})` : '';
  return `УК и адреса${name}`;
};

const getLabelByCityId = (cities, cityId) => {
  const cityName = cities?.find((city) => city.id === cityId)?.name;
  const name = cityName ? ` (${cityName})` : '';
  return `УК и адреса${name}`;
};

const AddressesField = ({ form, initialValue }) => {
  const queryClient = useQueryClient();

  const cityDependentData = useNewCityValueSubscription();
  const cityId = initialValue
    ? initialValue.city?.id
    : cityDependentData?.[cityFieldName];
  const companyDependentData = useNewCompanyValueSubscription();
  const companyId = initialValue
    ? initialValue.company?.id
    : companyDependentData?.[companyFieldName];

  const cities = queryClient.getQueryData(citiesKey(companyId));

  const init = useMemo(() => generateDataForTree(initialValue), [initialValue]);
  const [halfCheckedKeys, setHalfCheckedKeys] = useState(init.halfCheckedKeys);

  const { treeData, setTreeData, loading, rawData } = useAddressesTreeData({
    cityId,
    enabled: initialValue ? true : Boolean(cityId && companyId),
    companyId,
  });

  const onLoadData = useLoadTreeData({ treeData, setTreeData, cityId });

  useEffect(() => {
    if (cityDependentData && !loading && rawData?.length) {
      if (cityDependentData.hasOwnProperty(fieldName)) {
        const rawValues = cityDependentData[fieldName];
        const values = generateDataForTree(rawValues);
        setHalfCheckedKeys(values.halfCheckedKeys);
        form.setFieldsValue({ [fieldName]: values.checkedKeys });
      } else {
        form.resetFields([fieldName]);
      }
    }
  }, [cityDependentData, loading, rawData]);

  return (
    <Field
      fieldType={fieldTypes.treeCheckbox}
      form={form}
      name={fieldName}
      label={
        initialValue
          ? getLabelFromInitialValue(initialValue)
          : getLabelByCityId(cities, cityId)
      }
      decoratorOptions={{
        rules: [{ required: true, message: 'Выберите адрес', type: 'array' }],
        initialValue: init.checkedKeys?.length ? init.checkedKeys : [],
      }}
      fieldProps={{
        treeData: initialValue || cityId ? treeData : [],
        loadData: onLoadData,
        loading,
        theme: treeCheckThemes.gray,
        rootClassName: styles.field,
        halfCheckedKeys,
      }}
    />
  );
};

export default AddressesField;
